import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, Button, Drawer, Field, Icon, Input, Modal } from '../../design';
import { useToast } from '../../design/Toast';
import { fmtDateTime, fmtRelative } from '../../lib/format';
import s from '../operations/operations.module.css';
import { REMOTE_STATUS } from './KeysPage';

/** Detaliul unei cereri de deschidere; aprobarea (alt utilizator, step-up), respingerea sau retragerea. */
export function RemoteOpenDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { user, has, withStepUp } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const query = useQuery({
    queryKey: ['remote-open', id],
    queryFn: () => unwrap(api.GET('/remote-open-requests/{id}', { params: { path: { id } } })),
    refetchInterval: (q) =>
      q.state.data && (q.state.data.status === 'pending' || q.state.data.status === 'approved')
        ? 10_000
        : false,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['remote-open'] });
  };
  const approve = useMutation({
    mutationFn: () =>
      withStepUp((headers) =>
        unwrap(
          api.POST('/remote-open-requests/{id}/approve', {
            params: { path: { id }, header: headers },
          }),
        ),
      ),
    onSuccess: (r) => {
      if (r.status === 'executed')
        toast('Aprobată și executată: comanda de deschidere a fost trimisă lockerului.', 'success');
      else if (r.status === 'failed')
        toast(`Aprobată, dar execuția a eșuat: ${r.failureReason ?? 'motiv necunoscut'}`, 'error');
      else toast('Cererea a fost aprobată.', 'success');
      refresh();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const reject = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/remote-open-requests/{id}/reject', {
          params: { path: { id } },
          body: { reason: rejectReason.trim() || undefined },
        }),
      ),
    onSuccess: () => {
      toast(isRequester ? 'Cererea a fost retrasă.' : 'Cererea a fost respinsă.', 'success');
      setRejecting(false);
      refresh();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  const r = query.data;
  const isRequester = !!r && r.requesterId === user?.id;
  const canApprove = !!r && r.status === 'pending' && has('approve') && !isRequester;
  const canReject = !!r && r.status === 'pending' && (has('approve') || isRequester);

  return (
    <Drawer
      title={r ? `Cerere de deschidere · ${r.slotLabel}` : 'Cerere de deschidere'}
      onClose={onClose}
      footer={
        r && (
          <>
            {canReject && (
              <Button variant="danger" onClick={() => setRejecting(true)}>
                {isRequester && !has('approve') ? 'Retrage' : 'Respinge'}
              </Button>
            )}
            {canApprove && (
              <Button
                variant="primary"
                icon="shield"
                onClick={() => approve.mutate()}
                busy={approve.isPending}
              >
                Aprobă și deschide
              </Button>
            )}
            <Button onClick={onClose}>Închide</Button>
          </>
        )
      }
    >
      {!r && <p className="muted">{query.error ? errorMessage(query.error) : 'Se încarcă…'}</p>}
      {r && (
        <div className="stack">
          <div className="flex between">
            <Badge tone={REMOTE_STATUS[r.status].tone}>{REMOTE_STATUS[r.status].label}</Badge>
            {r.status === 'pending' && (
              <span className="small muted">expiră {fmtRelative(r.expiresAt)}</span>
            )}
          </div>
          <dl className={s.pairs}>
            <dt>Locker</dt>
            <dd>
              <Link to={`/lockere/${r.lockerId}`}>Deschide lockerul</Link>
            </dd>
            <dt>Compartiment</dt>
            <dd>{r.slotLabel}</dd>
            <dt>Solicitant</dt>
            <dd>{r.requesterLabel}</dd>
            <dt>Cerută</dt>
            <dd>{fmtDateTime(r.requestedAt)}</dd>
            <dt>Motiv</dt>
            <dd>{r.reason}</dd>
            <dt>Referință</dt>
            <dd>{r.ticket || '—'}</dd>
            {r.approverLabel && (
              <>
                <dt>Aprobator</dt>
                <dd>{r.approverLabel}</dd>
              </>
            )}
            {r.decidedAt && (
              <>
                <dt>Decisă</dt>
                <dd>{fmtDateTime(r.decidedAt)}</dd>
              </>
            )}
            {r.executedAt && (
              <>
                <dt>Executată</dt>
                <dd>{fmtDateTime(r.executedAt)}</dd>
              </>
            )}
            {r.failureReason && (
              <>
                <dt>Eșec</dt>
                <dd style={{ color: 'var(--red)' }}>{r.failureReason}</dd>
              </>
            )}
          </dl>
          {r.status === 'pending' && isRequester && (
            <div className={s.warn} role="note">
              <Icon name="shield" size={18} />
              <span>
                Aprobarea trebuie făcută de un alt utilizator autorizat, din propria sesiune;
                solicitantul nu își poate aproba cererea.
              </span>
            </div>
          )}
          {r.status === 'pending' && canApprove && (
            <div className={s.warn} role="note">
              <Icon name="alert" size={18} />
              <span>
                Aprobarea execută imediat deschiderea. Verifică motivul și compartimentul înainte de
                a confirma cu codul 2FA.
              </span>
            </div>
          )}
        </div>
      )}
      {rejecting && (
        <Modal
          title={isRequester && !has('approve') ? 'Retrage cererea' : 'Respinge cererea'}
          onClose={() => setRejecting(false)}
          footer={
            <>
              <Button onClick={() => setRejecting(false)}>Renunță</Button>
              <Button variant="danger" onClick={() => reject.mutate()} busy={reject.isPending}>
                Confirmă
              </Button>
            </>
          }
        >
          <Field label="Motiv (opțional)" htmlFor="ro-reject-reason">
            <Input
              id="ro-reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              maxLength={500}
            />
          </Field>
        </Modal>
      )}
    </Drawer>
  );
}
